/**
 * 고객사 대시보드 공통 레이아웃
 *
 * 인증 초기화를 여기서 처리하여, 어떤 하위 페이지로 직접 진입해도
 * 인증이 보장된 상태에서 렌더링된다.
 *
 * 흐름:
 * 1. URL 파라미터 확인 (access_token, Cafe24 HMAC, OAuth callback)
 * 2. sessionStorage 토큰 검증 (/v2/auth/me)
 * 3. 인증 완료 → shop 데이터 로딩 → authReady=true → Outlet 렌더
 */
import { Layout } from '@/components/Layout'
import { OwnerInfoModal } from '@/components/OwnerInfoModal'
import { CampusLinkRequiredScreen } from '@/features/campus-link/CampusLinkRequiredScreen'
import { LandingPage } from '@/features/landing/LandingPage'
import { ShopProvider } from '@/lib/shopContext'
import {
  useCampusLinkInstallStart,
  useGetCampusLinkForCurrentShop,
} from '@/api/generated/v2-campus-link/v2-campus-link'
import { useGetCatalogConfig } from '@/api/generated/v2-catalog/v2-catalog'
import { getGetShopQueryKey, useGetShop } from '@/api/generated/v2-shop/v2-shop'
import type {
  ShopResponse as OrvalShopResponse,
  CampusLinkMeResponse,
  CatalogConfigResponse,
} from '@/api/schemas'
import type { ShopInfoResponse, ShopResponse } from '@/types'
import { api, authApi } from '@/lib/api'
import { auth, setAuthBootstrapping } from '@/lib/auth'
import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

/**
 * "이 토큰은 죽었다" 로 볼 수 있는 실패인지. axios(`lib/api.ts`)와 fetch 래퍼
 * (`api/custom-instance.ts`)가 서로 다른 모양으로 던져서 둘 다 본다.
 * 판단이 서지 않으면 **아니라고 답한다** — 세션을 지우는 쪽이 더 비싼 오답이다.
 */
function isUnauthorized(error: unknown): boolean {
  const e = error as { status?: unknown; response?: { status?: unknown } }
  return e?.response?.status === 401 || e?.status === 401
}

/**
 * 어드민 임퍼소네이션 표시 (`admin/customers/$customerId.tsx` → `/?access_token=`).
 *
 * 아래 캠퍼스 연동 가드는 어드민을 면제한다 — 어드민은 **미연동 고객의 대시보드를
 * 열어보는 것이 일**이라, 고객용 연동 유도 화면에 갇히면 지원을 못 한다.
 *
 * URL 파라미터를 그대로 읽지 않고 표시를 남기는 이유: `initializeAuth` 가 토큰을
 * 저장하자마자 `history.replaceState` 로 `access_token` 을 URL 에서 지운다. 렌더
 * 시점에는 읽을 게 남아 있지 않고, 어드민이 새로고침하면 면제가 풀린다.
 *
 * ⚠️ **이 표시는 인증 수단이 아니다.** 사용자가 콘솔에서 직접 넣을 수 있는 값이고,
 * 애초에 라우트 가드 자체가 클라이언트에 있어 우회 가능한 1차 안내다. 실제 강제는
 * follow-up 인 API 403 가드가 진다 — 이 값으로 권한을 판단하지 마라.
 *
 * sessionStorage 라 탭을 닫으면 사라진다(면제 범위 = 어드민이 연 그 탭). 그리고
 * **그게 유일한 수명이다** — 아래 `?token=` 분기의 `clearAdminBootstrap()` 은 도달하는
 * 진입 경로가 실재하지 않는다(그 자리 주석 참조). 이 표시가 고객 로그인에 의해 지워질
 * 것이라고 가정하지 마라.
 */
const ADMIN_BOOTSTRAP_KEY = 'obps_admin_bootstrap'

/**
 * 저장소 접근은 **반드시 try/catch** — 프라이빗 모드·사이트 데이터 차단 설정에서
 * `sessionStorage` 접근 자체가 throw 한다. 여기서 터지면 어드민이 아니라 **모든
 * 사용자**의 대시보드 초기화가 죽으므로, 실패는 "면제 아님" 으로만 떨어뜨린다.
 */
function markAdminBootstrap(): void {
  try {
    sessionStorage.setItem(ADMIN_BOOTSTRAP_KEY, '1')
  } catch {
    // 저장 못 해도 진행한다 — 어드민이 새로고침하면 가드 화면을 볼 뿐이다.
  }
}

function clearAdminBootstrap(): void {
  try {
    sessionStorage.removeItem(ADMIN_BOOTSTRAP_KEY)
  } catch {
    // 무시
  }
}

function hasAdminBootstrap(): boolean {
  try {
    return sessionStorage.getItem(ADMIN_BOOTSTRAP_KEY) === '1'
  } catch {
    return false
  }
}

export const Route = createFileRoute('/_dashboard')({
  component: DashboardLayout,
})

/** 고객사 대시보드 공통 레이아웃 — 인증 + 사이드바 + ShopContext */
function DashboardLayout() {
  // === 인증 상태 ===
  const [authReady, setAuthReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // === Shop 컨텍스트 ===
  // shop 의 단일 소스는 아래 useGetShop 쿼리다. 이 상태는 그 결과를 컨텍스트로
  // 흘려보내는 통로일 뿐이라, 여기 말고 다른 곳에서 덮어쓰지 않는다.
  const [shop, setShop] = useState<ShopResponse | null>(null)
  const queryClient = useQueryClient()
  const [catalogConnected, setCatalogConnected] = useState(false)
  const [ownerInfoModalOpen, setOwnerInfoModalOpen] = useState(false)
  const openOwnerInfoModal = useCallback(() => setOwnerInfoModalOpen(true), [])

  // === Orval Query: 인증 완료 후 shop 로딩 ===
  // 모든 하위 페이지가 이 쿼리 하나에서 shop 을 받으므로, 실패를 스피너로
  // 뭉개지 않고 아래에서 에러 화면으로 끊는다.
  const { data: shopRaw, isError: shopError } = useGetShop({
    query: { enabled: authReady },
  })

  // 카탈로그 연동 상태 조회 (사이드바 메뉴 표시용)
  const { data: catalogConfigRaw } = useGetCatalogConfig({
    query: { enabled: !!shop },
  })

  // === 캠퍼스 연동 가드 ===
  // 설치 순서가 뒤집히면서(카페24 OAuth 가 맨 앞으로) **연동 없이도 설치가 끝난다.**
  // 예전에는 설치 앞단의 캠퍼스 로그인이 수강 자격을 봤기 때문에 대시보드에 도달한
  // 사람은 이미 검증된 상태였다. 그 자리를 여기가 이어받는다.
  //
  // 이 가드는 **두 번째 그물**이다 — 단일샵 첫 설치는 OAuth 콜백이 곧바로 캠퍼스
  // 연동 화면으로 보내고, 여기는 멀티샵·콘솔 재진입·직접 URL 경로를 덮는다.
  const {
    data: campusLinkRaw,
    isError: campusLinkFailed,
    isSuccess: campusLinkLoaded,
  } = useGetCampusLinkForCurrentShop({ query: { enabled: !!shop } })
  const campusLinkMe = (campusLinkRaw ?? null) as CampusLinkMeResponse | null

  // 어드민 임퍼소네이션 면제 표시. 초기값을 저장소에서 읽어 **새로고침해도 유지**한다
  // (`access_token` 은 아래 initializeAuth 가 URL 에서 즉시 지우므로 한 번 지나가면
  //  다시 읽을 수 없다). 값의 성격은 ADMIN_BOOTSTRAP_KEY 주석 참조 — 인증 아님.
  const [adminBootstrap, setAdminBootstrap] = useState(hasAdminBootstrap)

  // 연동 시작 — 성공하면 캠퍼스로 넘어가므로 화면을 다시 그릴 일이 없다.
  // `CampusLinkAccountMenuItem` 의 startMutation 과 같은 패턴.
  const installStartMutation = useCampusLinkInstallStart({
    mutation: {
      onSuccess: (response) => {
        const url = (response as { redirect_url?: string } | undefined)
          ?.redirect_url
        if (!url) {
          toast.error('연동 시작 URL을 받을 수 없습니다.')
          return
        }
        window.location.href = url
      },
      onError: (err: any) => {
        const message = err?.body?.message || err?.message || '연동 시작 실패'
        toast.error(message)
      },
    },
  })

  // Shop 데이터 → 컨텍스트 동기화
  useEffect(() => {
    const s = (shopRaw ?? null) as OrvalShopResponse | null
    if (s) setShop(s as any)
  }, [shopRaw])

  // 카탈로그 연동 상태 동기화
  useEffect(() => {
    const config = catalogConfigRaw as CatalogConfigResponse | undefined
    if (config) {
      setCatalogConnected(!!config.meta_catalog_id)
    }
  }, [catalogConfigRaw])

  // 대표자 정보 미입력 시 모달
  useEffect(() => {
    if (shop && (!shop.owner_name || !shop.owner_phone)) {
      openOwnerInfoModal()
    }
  }, [shop, openOwnerInfoModal])

  // === 캠퍼스 연동 가드 — fail-open 신호 ===
  // 가드가 **판단할 재료가 없어서** 통과시킨 경우에만 흔적을 남긴다. 이 가드의 실효성은
  // 곧 "얼마나 자주 fail-open 으로 새는가" 인데 지금은 통과와 차단이 구분 없이 조용해서
  // 잴 방법이 없고, follow-up 인 **API 403 가드**를 설계할 때 그 숫자가 필요하다.
  //
  // breadcrumb 이 아니라 captureMessage 를 쓴다 — breadcrumb 은 **뒤이어 에러가 잡힐 때만**
  // 그 이벤트에 첨부돼 전송된다. fail-open 은 화면이 멀쩡히 그려지는 경우라 대개 에러가
  // 따라오지 않아 한 건도 안 남고, 남는 표본은 "이미 터진 세션" 쪽으로 쏠린다. 세는 것이
  // 목적이므로 자기 이벤트를 만드는 captureMessage 가 맞다(`lib/metaCapi.ts` 가 같은
  // 이유로 warning 레벨 captureMessage 를 쓴다).
  //
  // ⚠️ **신호만 낸다 — 판정은 건드리지 않는다.** 통과 여부는 아래 `campusLinkPassed` 가
  // 그대로 정한다. 정상 통과(linked / exempt / 어드민)에는 아무것도 남기지 않는다(소음).
  //
  // ⚠️ **단위는 "사용자" 가 아니라 "페이지 로드" 다.** 여기는 레이아웃 라우트라 하위
  // 화면 이동으로는 다시 마운트되지 않고, 새로고침·재진입마다 1회 발화한다. 한 사람이
  // 하루에 여러 번 셀 수 있으므로 후속 설계에서 이 수를 사용자 수로 읽지 마라
  // (개발 빌드의 StrictMode 이중 발화는 별개 — 집계 시 environment 로 거른다).
  const shopReady = !!shop
  const shopMallId = shop?.cafe24_mall_id ?? null
  useEffect(() => {
    // 가드에 도달하지 못하는 상태는 대상이 아니다 — 샵 없음(랜딩), 조회 진행 중(스피너).
    if (!shopReady) return
    if (!campusLinkLoaded && !campusLinkFailed) return
    // 어드민 임퍼소네이션은 조회 결과와 무관하게 면제라 fail-open 으로 세지 않는다.
    if (adminBootstrap) return

    // 통과 사유가 fail-open 인 두 갈래만 구분해서 남긴다.
    //   query-failed : 조회 자체가 실패(500·타임아웃·오프라인)
    //   empty-body   : 200 인데 본문을 못 읽음(스키마 변경·프록시가 삼킴)
    //
    // **`isError` 만 보면 안 된다.** react-query 는 이전 성공 `data` 를 들고 있는 채로
    // `isError=true` 가 될 수 있다(캐시된 응답 + 재조회 실패 — `refetchOnReconnect` 기본
    // true, 연결 해제 후 invalidate 등). 그때 답을 **이미 알고 있는데도** fail-open 한
    // 건으로 찍히면, 이 숫자로 API 403 가드의 필요량을 잡을 때 과대평가가 된다.
    //
    // 그래서 신호와 판정이 갈린다 — 아래 `campusLinkPassed` 는 `campusLinkFailed` 를
    // 먼저 보고 무조건 통과시키지만(그게 fail-open 의 정의다), 여기서는 "통과를 **데이터가**
    // 정당화하는가" 를 묻는다. 정당화되면(linked·exempt) 세지 않고, 캐시가 linked:false 를
    // 말하는데도 재조회 실패로 통과한 건 진짜 누수라 센다.
    const justifiedByData =
      !!campusLinkMe && (!!campusLinkMe.linked || !!campusLinkMe.exempt)
    const reason = justifiedByData
      ? null
      : campusLinkFailed
        ? 'query-failed'
        : !campusLinkMe
          ? 'empty-body'
          : null
    if (!reason) return

    console.warn('[campus-guard] fail-open', { reason, mallId: shopMallId })
    Sentry.captureMessage('[campus-guard] fail-open', {
      level: 'warning',
      tags: { campus_guard: 'fail-open', campus_guard_reason: reason },
      extra: { mallId: shopMallId },
    })
  }, [
    shopReady,
    shopMallId,
    campusLinkLoaded,
    campusLinkFailed,
    campusLinkMe,
    adminBootstrap,
  ])

  // === 인증 초기화 (imperative — OAuth, HMAC, 토큰 전달 등 복잡한 로직) ===
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search)

        // 세션 만료 메시지 처리 — 만료되면 아래 랜딩으로 떨어진다.
        // 진입 경로를 **둘 다** 이름으로 부른다. auth.ts 의 handleSessionExpired 는
        // `/?session_expired=true` 로 보내면서 Cafe24 HMAC 파라미터를 떨궈서, 콘솔로
        // 들어온 사용자도 여기 도착한다 — 그들의 복구는 캠퍼스 로그인이 아니라
        // 앱 재실행이고, 랜딩에서 그 안내는 아래쪽 보조 카드에 있다.
        if (params.get('session_expired') === 'true') {
          toast.error(
            '세션이 만료되었어요. 카페24 관리자에서 앱을 다시 실행하거나, 캠퍼스 계정으로 로그인해주세요.',
            { duration: 6000 },
          )
          params.delete('session_expired')
          const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
          window.history.replaceState({}, '', newUrl)
        }

        // 어드민이 고객 대시보드를 여는 경로(`admin/customers/$customerId.tsx`).
        const accessToken = params.get('access_token')
        if (accessToken) {
          auth.setToken(accessToken)
          // 캠퍼스 연동 가드 면제 표시를 남긴다. 파라미터는 바로 아래에서 URL 에서
          // 지워지므로, 여기서 적어두지 않으면 렌더 시점에 어드민임을 알 길이 없다.
          markAdminBootstrap()
          setAdminBootstrap(true)
          params.delete('access_token')
          const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
          window.history.replaceState({}, '', newUrl)
          // 아래 분기와 같은 이유로 초기화 구간을 표시한다. 없으면 401 을 받은
          // 인터셉터가 `/?session_expired=true` 로 이동을 걸고, 곧이어 catch 의
          // logout() 이 `/` 로 또 걸어서 — 어느 쪽이 이기든 아래 안내 토스트는
          // 화면이 바뀌며 사라진다. 어드민은 이유 없이 랜딩에 떨어진다.
          setAuthBootstrapping(true)
          try {
            // 토큰 유효성만 확인한다 (응답 본문은 쓰지 않음) —
            // shop 상세는 아래 useGetShop 이 채운다
            await api.get<ShopInfoResponse>('/v2/auth/me', {
              headers: { Authorization: `Bearer ${accessToken}` },
            })
            setAuthReady(true)
            setLoading(false)
            return
          } catch {
            auth.clearToken()
            toast.error('유효하지 않은 토큰입니다.')
            setLoading(false)
            return
          } finally {
            setAuthBootstrapping(false)
          }
        }

        // OAuth 콜백 (token 파라미터)
        const oauthToken = params.get('token')
        if (oauthToken) {
          auth.setToken(oauthToken)
          // 새 고객 세션이 이 탭에 들어왔으니 어드민 면제 표시를 지운다. 안 지우면
          // 어드민이 쓰던 탭에서 고객이 로그인했을 때 면제가 그대로 따라붙는다.
          //
          // ⚠️ 현재 `/?token=` 진입은 실재하지 않는다(모든 OAuth 성공은 /auth/success 를
          // 거친다 — 백엔드 두 곳 다 `{FRONTEND_URL}/auth/success?token=` 로 보내고,
          // `auth.success.tsx`·`shop-select.tsx`·`campus-sso/select.tsx` 는 각자 토큰을
          // 저장한 뒤 파라미터 없이 `/` 로 이동한다). 즉 이 clear 는 한 번도 실행되지
          // 않는다. 방어적 보험으로만 남긴다 — 실제 면제 수명은 "어드민이 연 탭이
          // 닫힐 때까지" 다.
          clearAdminBootstrap()
          setAdminBootstrap(false)
          params.delete('token')
          const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
          window.history.replaceState({}, '', newUrl)
        }

        // Cafe24 관리자 화면에서 호출된 경우
        const mall_id = params.get('mall_id')
        const timestamp = params.get('timestamp')
        const hmac = params.get('hmac')

        if (mall_id && timestamp && hmac) {
          const cafe24Params = {
            auth_config: params.get('auth_config'),
            is_multi_shop: params.get('is_multi_shop'),
            lang: params.get('lang'),
            mall_id,
            nation: params.get('nation'),
            shop_no: params.get('shop_no'),
            timestamp,
            user_id: params.get('user_id'),
            user_name: params.get('user_name'),
            user_type: params.get('user_type'),
            hmac,
          }

          // 다른 두 분기와 같은 표시. **여기가 제일 필요하다** — 백엔드
          // `_validate_timestamp` 가 ±2시간을 벗어난 timestamp 를 401 로 던지는데,
          // 세션 JWT 는 7일이라 "콘솔 탭을 열어둔 채 두 시간 뒤 새로고침" 이 흔하다.
          // 표시가 없으면 그 401 을 인터셉터가 세션 만료로 보아 **아직 멀쩡한 토큰을
          // 지우고** 랜딩으로 튕기고, 아래 "Cafe24 인증에 실패했어요" 안내는 화면이
          // 바뀌며 사라진다.
          setAuthBootstrapping(true)
          try {
            const authResult = await authApi.validateCafe24Auth(cafe24Params)
            if (authResult.valid) {
              const requestedShopNo = cafe24Params.shop_no ? parseInt(cafe24Params.shop_no) : 1

              if (auth.isAuthenticated()) {
                try {
                  const shopData = await authApi.getMe()
                  const isSameMall = shopData.cafe24_mall_id === mall_id
                  const isSameShop = shopData.shop_no === requestedShopNo

                  if (isSameMall && isSameShop) {
                    setAuthReady(true)
                  } else {
                    auth.logout()
                    authApi.startOAuthWithMallId(mall_id, requestedShopNo)
                    return
                  }
                } catch {
                  auth.logout()
                  authApi.startOAuthWithMallId(mall_id, requestedShopNo)
                  return
                }
              } else {
                authApi.startOAuthWithMallId(mall_id, requestedShopNo)
                return
              }
            } else {
              throw new Error(`Cafe24 인증 실패: ${authResult.message}`)
            }
          } catch {
            setError('Cafe24 인증에 실패했어요. 관리자 페이지에서 다시 시도해주세요.')
            setLoading(false)
            return
          } finally {
            setAuthBootstrapping(false)
          }
        } else {
          // 토큰이 있을 때만 검증한다. 가드 없이 부르면 미인증 방문이 401 을 받고,
          // axios 인터셉터가 그걸 세션 만료로 보아 `/?session_expired=true` 로 보낸다 —
          // 거기서 또 토큰 없이 부르므로 랜딩이 무한 반복된다.
          if (auth.isAuthenticated()) {
            // 이 구간의 401 은 "끊겼다" 가 아니라 "애초에 아니다" 다. 표시해 두지 않으면
            // 인터셉터가 만료 안내로 루트에 튕겨서, 보던 딥링크를 잃는다.
            setAuthBootstrapping(true)
            try {
              await authApi.getMe()
              setAuthReady(true)
            } catch (e) {
              // **401 일 때만 토큰을 버린다.** 전에는 실패면 무조건 버려서, 오프라인·
              // 타임아웃·배포 중 502 같은 일시적 실패에도 멀쩡한 세션이 끊겼다.
              // /v2/auth/me 가 잠깐 흔들리면 그 사이 앱을 연 사람 전부가 다시
              // 로그인해야 한다 — 이 티켓이 없애려던 재로그인을 되레 만드는 셈이다.
              if (isUnauthorized(e)) {
                // 토큰을 버리되 **이동하지 않는다.** 아래에서 이 경로 그대로 랜딩이
                // 그려지고, 로그인 버튼이 지금 경로를 복귀 지점으로 적는다. logout() 으로
                // `/` 에 보내면 딥링크로 들어온 사람이 어디로 돌아갈지가 그 순간 사라진다.
                auth.clearToken()
              } else {
                // 토큰은 그대로 두고 재시도할 수 있는 화면으로 보낸다.
                setError('서버에 연결하지 못했어요. 잠시 후 다시 시도해주세요.')
              }
            } finally {
              setAuthBootstrapping(false)
            }
          }
        }

        setLoading(false)
      } catch {
        setError('초기화 중 오류가 발생했어요.')
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // === 상태 화면들 ===

  // shop 조회 실패 — 재시도 후에도 못 받았으면 스피너에 갇히지 않게 에러로 끊는다
  const shopLoadFailed = authReady && shopError && !shop

  // 캠퍼스 연동 조회가 아직 안 끝난 구간(react-query 재시도 1회 포함).
  // 여기서 기다리지 않으면 대시보드를 먼저 그렸다가 가드 화면으로 갈아끼우게 되어
  // 화면이 번쩍인다. `enabled: !!shop` 이라 shop 이 없으면 애초에 해당 없다
  // (그 경우는 위의 shop 로딩/랜딩 분기가 처리한다).
  //
  // 대기 판정에 **데이터 유무가 아니라 쿼리 상태**를 쓴다 — 본문이 빈 성공 응답이
  // 오면 데이터 기준으로는 영원히 "로딩 중" 이 되어 스피너에 갇힌다.
  const campusLinkPending = !!shop && !campusLinkLoaded && !campusLinkFailed

  // 인증 진행 중이거나, 인증 완료 후 shop·연동 상태 로딩 중일 때 스피너 표시
  if (loading || (authReady && !shop && !shopLoadFailed) || campusLinkPending) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-on-surface-variant">로그인 상태 확인 중...</p>
        </div>
      </div>
    )
  }

  // 인증 초기화 오류 + shop 조회 실패를 같은 화면으로 안내한다 (둘 다 새로고침이 처방)
  const errorMessage =
    error ??
    (shopLoadFailed
      ? '샵 정보를 불러오지 못했어요. 잠시 후 다시 시도해주세요.'
      : null)

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <div className="max-w-md w-full mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-error">
                <AlertCircle className="h-5 w-5 mr-2" />
                오류 발생
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-on-surface-variant">{errorMessage}</p>
              <Button onClick={() => window.location.reload()} variant="secondary" className="w-full">
                새로고침
              </Button>
              {/* 탈출구. 이 화면은 `!shop` 랜딩보다 먼저 걸리고 토큰은 일부러 남겨두므로,
                  실패가 계속되면(지속 500·CORS 로 지워진 응답·캡티브 포털) 새로고침 말고는
                  아무 데도 못 간다. 로그인 버튼에 닿을 방법조차 없다. */}
              <Button onClick={() => auth.logout()} variant="outline" className="w-full">
                로그아웃하고 다시 로그인
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // 미인증 — 서비스 소개 + 로그인 랜딩.
  // 예전에는 "Cafe24 관리자 콘솔에서 접속해주세요" 잠금 카드였는데, 그 화면에는
  // 로그인할 방법 자체가 없어서 솔루션 주소를 직접 연 사용자가 막혔다.
  if (!shop) {
    return <LandingPage />
  }

  // === 캠퍼스 연동 가드 ===
  // 통과 조건은 넷. **하나라도 참이면 대시보드를 그린다.**
  //   1) 연동됨
  //   2) 화이트리스트 면제 (`exempt`) — 설치 게이트에 있던 화이트리스트가 이리로 왔다
  //   3) 어드민 임퍼소네이션 (ADMIN_BOOTSTRAP_KEY 주석 참조)
  //   4) **조회 실패 — fail-open**
  //
  // ⚠️ 4번이 이 가드에서 제일 중요한 결정이다. `/v2/auth/campus-link/me` 가 500·
  // 타임아웃·오프라인으로 흔들리는 순간 fail-closed 를 택하면 **전 고객이** 연동
  // 유도 화면을 보고 캠퍼스로 몰려간다(prod 활성 몰 178개 중 152개가 이미 연동돼
  // 있다 — 전부 오탐이다). 위 `initializeAuth` 가 같은 이유로 401 일 때만 토큰을
  // 버리는 것과 같은 판단이다: 판단이 서지 않으면 **막지 않는 쪽**으로 답한다.
  //
  // 막지 못해 새는 만큼의 책임은 이 화면이 지지 않는다 — 강제의 최종 책임은
  // follow-up 인 **API 403 가드**(서버 측)가 지고, 이 라우트 가드는 1차 안내다.
  //
  // 4번으로 새는 횟수는 위의 "fail-open 신호" useEffect 가 Sentry 에 남긴다
  // (`campus_guard:fail-open`). 그게 얼마나 새는지 아는 유일한 계기다.
  const campusLinkPassed =
    // 판단할 재료가 없으면 막지 않는다 — 조회 실패(4)와, 본문을 못 읽은 성공 응답.
    campusLinkFailed ||
    !campusLinkMe ||
    adminBootstrap ||
    !!campusLinkMe.linked ||
    !!campusLinkMe.exempt

  if (!campusLinkPassed) {
    // Layout·Outlet 없이 전체화면으로 — 사이드바를 남기면 "연동 안 해도 쓸 수 있는
    // 곳이 있다" 로 읽힌다 (하드 가드, 아론 결정).
    return (
      <CampusLinkRequiredScreen
        onStart={() => installStartMutation.mutate()}
        loading={installStartMutation.isPending}
      />
    )
  }

  // 인증 완료 + shop 로딩 완료 → 레이아웃 렌더
  return (
    <ShopProvider value={{ shop, openOwnerInfoModal, catalogConnected, setCatalogConnected }}>
      <Layout shop={shop} catalogConnected={catalogConnected} onEditOwnerInfo={openOwnerInfoModal}>
        <Outlet />
      </Layout>

      {/* 대표자 정보 모달 */}
      <OwnerInfoModal
        open={ownerInfoModalOpen}
        initialName={shop.owner_name ?? undefined}
        initialPhone={shop.owner_phone ?? undefined}
        onComplete={() => {
          // 저장은 모달이 이미 마쳤다. 상태를 직접 덮어쓰지 않고 shop 쿼리를 무효화해
          // 서버 값으로 다시 채운다 (컨텍스트/쿼리 캐시가 어긋나지 않게).
          // 모달은 refetch 를 기다리지 않고 즉시 닫는다 — 모달이 onComplete 를 await
          // 하지 않아서, 기다리면 저장 버튼이 다시 활성화된 채 모달이 떠 있게 된다.
          setOwnerInfoModalOpen(false)
          void queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        }}
        onSkip={() => setOwnerInfoModalOpen(false)}
      />
    </ShopProvider>
  )
}
